import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@core-ui/Components/ui/table';
import { Badge } from '@core-ui/Components/ui/badge';
import { cn } from '@core-ui-lib/utils';

import { EmptyState } from '@ui/shared/emptyState';

import type { DialingContact } from '../types/wizard.types';
import { ContactsTableSkeleton } from '@features/contacts';

interface SessionContactsTableProps {
  contacts: DialingContact[];
  isLoading?: boolean;
}

type CallStatus = 'pending' | 'called' | 'dialing' | 'voicemail' | 'transferred' | 'answered' | 'completed' | 'failed' | 'cut_off';

const statusColors: Record<CallStatus, string> = {
  transferred: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300',
  called: 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300',
  dialing: 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300',
  answered: 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300',
  voicemail: 'bg-violet-500/10 text-violet-700 border-violet-500/30 dark:text-violet-300',
  completed: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300',
  cut_off: 'bg-orange-500/10 text-orange-700 border-orange-500/30 dark:text-orange-300',
  failed: 'bg-red-500/10 text-red-700 border-red-500/30 dark:text-red-300',
  pending: 'bg-slate-500/10 text-slate-600 border-slate-500/30 dark:text-slate-400',
};

const getStatusBadge = (status?: string): { className: string; label: CallStatus } => {
  const normalized = (status ?? 'pending').toLowerCase() as CallStatus;
  const validStatus = Object.keys(statusColors).includes(normalized) ? normalized : 'pending';

  return {
    className: statusColors[validStatus],
    label: validStatus,
  };
};

/**
 * Notes for one contact: clamped to two lines, expandable in place.
 *
 * This replaced a hover tooltip. On a table the SDR reads DURING a call, a
 * tooltip is the wrong shape: it floated over the rows underneath, so reading
 * one contact's notes hid the next contact's; it needed a steady hover, which
 * is awkward while dialling and impossible on touch; and it repeated text that
 * was already legible in the cell.
 *
 * Expanding in place covers nothing, survives a click, and is reachable by
 * keyboard. The expanded block scrolls at max-h-40 rather than growing without
 * bound, so one long note cannot push the rest of the session off-screen.
 */
const NotesCell = ({
  t,
  notes,
  notesAgent,
  notesSDR,
}: {
  t: (key: string) => string;
  notes?: string;
  notesAgent?: string;
  notesSDR?: string;
}): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isClamped, setIsClamped] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  const lines = [
    { key: 'notes', label: t('wizard.dashboard.table.notes'), value: notes },
    { key: 'agent', label: t('wizard.dashboard.table.agentNotes'), value: notesAgent },
    { key: 'sdr', label: t('wizard.dashboard.table.sdrNotes'), value: notesSDR },
  ].filter((line) => line.value != null && line.value !== '');

  // Whether the clamp is actually hiding something. Measured rather than
  // guessed from the text length: the column is a percentage of the table, so
  // the same note fits on one screen and wraps on another.
  //
  // Only measured while collapsed — expanded, scrollHeight equals clientHeight
  // and the toggle would disappear just as it is needed to collapse again.
  React.useLayoutEffect(() => {
    const element = bodyRef.current;

    if (element === null || isExpanded) {
      return;
    }

    const measure = (): void => {
      setIsClamped(element.scrollHeight > element.clientHeight + 1);
    };

    measure();

    // Re-measure on column resize, or the toggle keeps the answer it got at
    // the width the table happened to have on first render.
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isExpanded, notes, notesAgent, notesSDR]);

  const toggle = (): void => {
    setIsExpanded((expanded) => !expanded);
  };

  if (lines.length === 0) {
    return <span className="text-muted-foreground/60">{t('wizard.dashboard.table.noNotes')}</span>;
  }

  return (
    <div className="space-y-1">
      <div
        ref={bodyRef}
        className={cn(
          'space-y-1 whitespace-pre-line break-words',
          isExpanded ? 'max-h-40 overflow-y-auto pr-1' : 'line-clamp-2',
        )}
      >
        {lines.map((line) => (
          <div key={line.key}>
            <span className="font-medium text-foreground/80">{line.label}:</span> {line.value}
          </div>
        ))}
      </div>

      {isClamped && (
        <button
          type="button"
          onClick={toggle}
          className="text-xs font-medium text-primary hover:underline"
          aria-expanded={isExpanded}
        >
          {isExpanded
            ? t('wizard.dashboard.table.showLess')
            : t('wizard.dashboard.table.showMore')}
        </button>
      )}
    </div>
  );
};


export default function SessionContactsTable ({
  contacts,
  isLoading = false,
}: SessionContactsTableProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="w-full max-h-140 overflow-auto rounded-md border border-border bg-card">
      <Table className="table-fixed">
        <TableHeader className="bg-muted/60">
          <TableRow className="border-b border-border/60">
            <TableHead className="w-[15%] min-w-[120px]">{t('wizard.dashboard.table.name')}</TableHead>
            <TableHead className="w-[15%] min-w-[120px]">{t('wizard.dashboard.table.company')}</TableHead>
            <TableHead className="w-[18%] min-w-[140px]">{t('wizard.dashboard.table.position')}</TableHead>
            <TableHead className="w-[14%] min-w-[130px]">{t('wizard.dashboard.table.phone')}</TableHead>
            <TableHead className="w-[25%] min-w-[150px]">{t('wizard.dashboard.table.notes')}</TableHead>
            <TableHead className="w-[13%] min-w-[110px] text-right">{t('wizard.dashboard.table.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <ContactsTableSkeleton rows={3} />
          ) : contacts.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-10">
                <EmptyState message={t('wizard.dashboard.noContacts')} />
              </td>
            </tr>
          ) : (
            contacts.map((contact, index) => {
              const statusBadge = getStatusBadge(contact.finalStatus);
              const rowKey = contact.id !== '' ? contact.id : `${contact.phone ?? ''}-${index}`;

              return (
                <TableRow
                  key={rowKey}
                  className={cn(
                    'border-b border-border hover:bg-muted/30',
                    statusBadge.label === 'transferred' && 'bg-emerald-500/5',
                    statusBadge.label === 'called' && 'bg-amber-500/5',
                    statusBadge.label === 'dialing' && 'bg-blue-500/5',
                    statusBadge.label === 'answered' && 'bg-blue-500/5',
                  )}
                >
                  <TableCell className="font-medium truncate">
                    {(contact.name ?? '') !== '' ? contact.name : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate">
                    {(contact.accountName ?? '') !== '' ? contact.accountName : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate">
                    {(contact.jobTitle ?? '') !== '' ? contact.jobTitle : '—'}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {(contact.phone ?? '') !== '' ? contact.phone : '—'}
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    <NotesCell
                      t={t}
                      notes={contact.notes}
                      notesAgent={contact.notesAgent}
                      notesSDR={contact.notesSdr}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge
                      variant="outline"
                      className={cn('capitalize border text-xs px-2 py-0.5', statusBadge.className)}
                    >
                      {t(`wizard.dashboard.status.${statusBadge.label}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
